import { PageHero, SectionHeader } from "@/components/Section";
import { church, fullAddress } from "@/config/church";
import useReveal from "@/hooks/useReveal";
import { Link } from "react-router-dom";
import {
  BookOpen,
  HeartHandshake,
  Cross,
  Flame,
  Users,
  Compass,
  ArrowRight,
  Music,
  Clock,
  Baby,
} from "lucide-react";

const values = [
  { icon: Cross, title: "Christ Above All", body: "Jesus is our foundation, our message, and our aim in everything." },
  { icon: BookOpen, title: "Word-Driven", body: "The Scriptures shape our doctrine, our worship, and our daily living." },
  { icon: HeartHandshake, title: "Family in Christ", body: "We treat one another as beloved brothers and sisters redeemed by grace." },
  { icon: Flame, title: "Spirit-Empowered", body: "We depend on the Holy Spirit for boldness, conviction, and transformation." },
  { icon: Users, title: "Every Generation", body: "From the nursery to the senior saints, every soul matters and belongs." },
  { icon: Compass, title: "Sent to Serve", body: "Commissioned to love Memphis and disciple the nations." },
];

const sundayFlow = [
  { icon: Clock, t: "10:00 AM", body: "Sunday School — age-appropriate biblical teaching for every generation." },
  { icon: Music, t: "11:00 AM", body: "Morning Worship — Spirit-filled praise, prayer, and preaching from Pastor Little." },
  { icon: Baby, t: "During Worship", body: "Safe, joyful children's ministry with secure check-in for our youngest disciples." },
];

export default function About() {
  useReveal();
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About New Philadelphia"
        title="Who we are."
        subtitle="A Christ-centered, historically Black Baptist congregation in East Memphis — anchored in the ancient path and pressing forward in the transforming power of Jesus."
        image="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/vxoldu53_616955902_122105707497216091_3578243584108528149_n.jpg"
      />

      {/* Who we are */}
      <section className="bg-white py-24">
        <div className="container-npbc grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
          <div data-reveal>
            <div className="eyebrow">Who We Are</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.05]">
              A house of worship, a family in Christ.
            </h2>
            <p className="mt-6 text-npbc-gray leading-relaxed text-[17px]">
              New Philadelphia Missionary Baptist Church is a historically African American Missionary Baptist
              congregation in East Memphis. For more than a century, our doors have been open at
              <span className="font-semibold text-npbc-black"> 533 S. Mendenhall Road</span> — a
              gathering place for worship, discipleship, mutual encouragement, and love of neighbor.
            </p>
            <p className="mt-4 text-npbc-gray leading-relaxed text-[17px]">
              Under the pastoral care of <span className="font-semibold text-npbc-black">{church.pastor.name}</span>,
              our Senior Pastor, we honor the legacy of the saints who built this house while embracing
              the fresh move of the Spirit in our generation.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/legacy" data-testid="about-legacy-cta" className="btn-primary">
                Explore Our Legacy <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/future" data-testid="about-future-cta" className="btn-ghost-dark">
                Our Future
              </Link>
            </div>
          </div>
          <div data-reveal className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-npbc-red/10 to-npbc-gold/15 -rotate-1" />
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/5tasfvv2_IMG_5564.jpg"
              alt="Congregation standing in worship at New Philadelphia"
              className="relative rounded-3xl w-full h-[520px] object-cover shadow-2xl"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="bg-npbc-cream py-24">
        <div className="container-npbc grid md:grid-cols-2 gap-6">
          <div data-reveal className="card-elevated p-10">
            <div className="eyebrow">Mission</div>
            <h3 className="mt-3 heading-serif text-4xl leading-tight">Preach. Disciple. Send.</h3>
            <p className="mt-4 text-npbc-gray leading-relaxed">
              To make disciples of Jesus Christ who love God, love one another, and love our neighbors —
              bringing hope and healing to Memphis and the nations.
            </p>
          </div>
          <div data-reveal className="card-elevated p-10 bg-npbc-black text-white">
            <div className="eyebrow !text-npbc-gold">Vision</div>
            <h3 className="mt-3 heading-serif text-4xl leading-tight">
              A church where tradition meets transformation.
            </h3>
            <p className="mt-4 text-white/75 leading-relaxed">
              Anchored to the historic African American Missionary Baptist tradition — expectant for the
              fresh move of the Holy Spirit in this generation.
            </p>
          </div>
        </div>
      </section>

      {/* Core values */}
      <section className="bg-white py-24">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader eyebrow="Core Values" title="What we stand on. What we live by." />
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {values.map((v, i) => (
              <div
                key={v.title}
                data-reveal
                style={{ transitionDelay: `${i * 60}ms` }}
                className="group card-elevated p-8 hover:-translate-y-1 transition-transform duration-500"
              >
                <div className="h-12 w-12 rounded-2xl bg-npbc-red/10 text-npbc-red grid place-items-center group-hover:bg-npbc-red group-hover:text-white transition">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 heading-serif text-2xl">{v.title}</h3>
                <p className="mt-2 text-npbc-gray leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to expect Sunday */}
      <section className="bg-npbc-cream py-24">
        <div className="container-npbc grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
          <div data-reveal>
            <div className="eyebrow">Your First Sunday</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.05]">
              What to expect when you visit.
            </h2>
            <p className="mt-6 text-npbc-gray leading-relaxed text-[17px]">
              Guests are our honored family. You'll be met at the door by warm greeters, offered a seat
              wherever feels comfortable, and welcomed into worship rooted in the Word and lifted in
              anointed song. There is no dress code — come as you are.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/visit" className="btn-primary">Plan Your Visit <ArrowRight className="h-4 w-4" /></Link>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`}
                target="_blank"
                rel="noreferrer noopener"
                className="btn-ghost-dark"
              >
                Get Directions
              </a>
            </div>
          </div>
          <div data-reveal className="space-y-4">
            {sundayFlow.map((s, i) => (
              <div key={s.t} style={{ transitionDelay: `${i * 60}ms` }} className="card-elevated p-6 flex items-start gap-5">
                <div className="h-11 w-11 rounded-2xl bg-npbc-red/10 text-npbc-red grid place-items-center shrink-0">
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-npbc-red">{s.t}</div>
                  <p className="mt-1 text-npbc-black leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Invitation */}
      <section className="bg-npbc-black text-white py-20">
        <div className="container-npbc text-center max-w-3xl mx-auto" data-reveal>
          <div className="eyebrow !text-npbc-gold justify-center flex">An Open Invitation</div>
          <h3 className="mt-3 heading-serif text-4xl md:text-5xl leading-tight">
            You were not brought here by accident.
          </h3>
          <p className="mt-4 text-white/75">
            Whether you're looking for a spiritual home, exploring faith for the first time,
            or simply passing through — there is a chair for you at New Philadelphia.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/visit" className="btn-gold">Plan Your Visit</Link>
            <Link to="/legacy" className="btn-outline">Our Legacy</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
